import React, { useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  OnChangeFn,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import TickCheckbox from '../../../components/Checkbox';
import { EntityCategories } from '../../../components/Categories';
import { IApplyFieldTag, IDictionaryFieldWithRowExpand } from './types';
import * as FormUI from '../../pageForm/FormUI';
import Table from '../../../components/Table';
import { fieldsColumnsConfig } from './tableStylesConfig';
import { FavoriteField } from '../../../components/Favorite';
import styled from 'styled-components';
import SvgIcon from '../../../components/Icon/SvgIcon';
import DerivedDefinitionIcon from '../../../components/DerivedDefinitionIcon';
import { HeaderCheckbox } from '../../../components/Table/TableCheckbox';

interface IProps {
  fields: IDictionaryFieldWithRowExpand[];
  schemaId: string;
  onApplyTags: (args: IApplyFieldTag) => Promise<void>;
  onSetFavorite: () => void;
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
  isPermittedToAssignCategory: boolean;
}

const initialState = {
  sorting: [
    {
      id: 'key',
      desc: false,
    },
  ],
};

const IndentedTickCheckbox = styled(TickCheckbox)<{ $indent?: number }>`
  margin-left: ${({ $indent }) => ($indent ?? 0) * 8}px;
`;
export const TableList = styled.ul`
  margin: 0;
  padding-left: 16px;
`;

const columnHelper = createColumnHelper<IDictionaryFieldWithRowExpand>();

const FieldsTable = ({
  fields,
  schemaId,
  rowSelection,
  setRowSelection,
  onApplyTags,
  onSetFavorite,
  isPermittedToAssignCategory,
}: IProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor('column', {
        id: 'key',
        header: ({ table }) => (
          <FormUI.CheckItemGroup>
            <HeaderCheckbox table={table} />
            Key
          </FormUI.CheckItemGroup>
        ),
        // custom cell checkbox for expandable rows
        cell: (info) => {
          const column = info.getValue();
          const row = info.row;

          return (
            <FormUI.CheckItemGroup>
              {row.getCanExpand() ? (
                <SvgIcon
                  name="es-array-object"
                  isHoverable
                  onClick={info.row.getToggleExpandedHandler()}
                />
              ) : (
                <IndentedTickCheckbox
                  $indent={row.depth}
                  type="checkbox"
                  checked={row.getIsSelected()}
                  onClick={row.getToggleSelectedHandler()}
                />
              )}
              <FormUI.OverflowingTextWrapper
                $isBold={info.row.getCanExpand()}
                $isClickable={info.row.getCanExpand()}
                onClick={info.row.getToggleExpandedHandler()}
              >
                {column}
              </FormUI.OverflowingTextWrapper>
            </FormUI.CheckItemGroup>
          );
        },
        meta: fieldsColumnsConfig.name,
        enableSorting: true,
      }),
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
        cell: (info) => {
          const name = info.getValue();
          const isDerived = !!info.row.original.derivedDefinition;
          return (
            <FormUI.OverflowingTextWrapper $isBold={true}>
              <FormUI.IconWithItemWrapper>
                {isDerived && (
                  <DerivedDefinitionIcon tooltipText={'Derived Field'} />
                )}
                {name}
              </FormUI.IconWithItemWrapper>
            </FormUI.OverflowingTextWrapper>
          );
        },
        meta: fieldsColumnsConfig.name,
        enableSorting: true,
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: 'Description',
        cell: (info) => {
          const description = info.getValue();

          return (
            <FormUI.OverflowingTextWrapper>
              {description}
            </FormUI.OverflowingTextWrapper>
          );
        },
        meta: fieldsColumnsConfig.description,
        enableSorting: true,
      }),
      columnHelper.accessor('tableNames', {
        id: 'tableNames',
        header: 'Tables',
        cell: (info) => {
          const tableNames = info.getValue();
          if (tableNames.length > 0) {
            return (
              <FormUI.OverflowingTextWrapper>
                <TableList>
                  {tableNames.map((table) => (
                    <li key={table}>{table}</li>
                  ))}
                </TableList>
              </FormUI.OverflowingTextWrapper>
            );
          }

          return null;
        },
        meta: fieldsColumnsConfig.tables,
      }),
      columnHelper.accessor('key', {
        id: 'tags',
        header: 'Tags',
        cell: (info) => {
          const categories = info.row.original.categories;

          return (
            <EntityCategories
              schemaId={schemaId}
              isDisabled={!isPermittedToAssignCategory}
              entityCategories={categories}
              onApply={(newTags, isManaged) =>
                onApplyTags({
                  entities: [info.row.original],
                  newTags,
                  isManaged,
                  schemaId,
                })
              }
              isManaged={info.row.original.isManaged}
            />
          );
        },
        meta: fieldsColumnsConfig.tags,
        enableSorting: true,
      }),
      columnHelper.accessor('isFavorite', {
        id: 'favorite',
        header: 'Favorite',
        cell: (info) => {
          const isFavorite = info.getValue();
          const key = info.row.original.key;

          return (
            <FavoriteField
              schema={schemaId}
              field={{ key, isFavorite }}
              onRefresh={onSetFavorite}
            />
          );
        },
        meta: fieldsColumnsConfig.favorite,
        enableSorting: true,
      }),
    ],
    [isPermittedToAssignCategory, onApplyTags, onSetFavorite, schemaId],
  );

  const table = useReactTable({
    data: fields,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
    initialState,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (originalRow) =>
      originalRow.rowExpand.hasRowExpand
        ? originalRow.rowExpand.subRows
        : undefined,
    getRowCanExpand: (row) => row.original.rowExpand.hasRowExpand,
    // using custom row ID because rowSelection contains sub rows which by default
    // have keys "<parentRowIndex>.<subRowIndex>" which is not
    // convenient for retrieving selected rows (we must parse the key). Using the field key
    // allows us to find the field directly
    getRowId: (originalRow) => originalRow.key,
  });

  return (
    <Table
      headerGroups={table.getHeaderGroups()}
      rows={table.getRowModel().rows}
    />
  );
};

export default FieldsTable;
